import { CampingSpot } from '../types';

const normalizeText = (value?: string) => String(value || '').trim().toLowerCase();

export const getSpotSource = (spot: Partial<CampingSpot>): 'nvdb' | 'osm' | 'user' | 'ai' | undefined => {
  if (spot.source) return spot.source;
  const features = Array.isArray(spot.features) ? spot.features.map((f) => String(f).toUpperCase()) : [];

  if (features.includes('NVDB') || spot.spotType === 'rasteplass') return 'nvdb';
  if (features.includes('OSM') || spot.spotType === 'osm_amenity' || spot.spotType === 'commercial_campsite' || spot.spotType === 'coastal') return 'osm';
  if (spot.createdBy || spot.sourceLevel === 4 || spot.spotType === 'confirmed') return 'user';
  if (spot.spotType === 'algorithmic') return 'ai';
  return undefined;
};

// Kategorier som IKKE er overnattings-/campingplasser, men informasjons-/
// faremarkører (bom, forbudt-sone) eller attraksjoner. Disse skal ALDRI få en
// AI-generert «flott fricamp-plass»-beskrivelse — den blir feilaktig og
// villedende (en bom er ikke et sted å campe). For slike markører vises bare
// det brukeren eventuelt selv skriver.
export const NON_DESCRIPTIVE_CATEGORY_IDS = ['bom', 'camping_forbudt', 'attraksjon'];
const NON_DESCRIPTIVE_FEATURES = ['BOM', 'CAMPING_FORBUDT'];

export const shouldAutoGenerateSpotDescription = (spot: Partial<CampingSpot>): boolean => {
  if (spot.spotType === 'attraction' || spot.spotType === 'forbidden_area') return false;
  // Kommersielle campingplasser/bobilplasser skal IKKE få villmarks-heuristikk-
  // beskrivelsen (veitype/underlag/nærmeste hus/score er meningsløst for en
  // kommersiell plass og gir generisk fyll-tekst). Beskrivelsen deres hentes fra
  // Googles editorialSummary (fetchGooglePlacePhotos) eller grunnes i backfill.
  if (spot.spotType === 'commercial_campsite') return false;
  if ((spot as any).barrierMetadata) return false;
  const features = Array.isArray(spot.features) ? spot.features.map((f) => String(f).toUpperCase()) : [];
  if (features.some((f) => NON_DESCRIPTIVE_FEATURES.includes(f))) return false;
  return true;
};

export const getExternalIdentityKey = (spot: Partial<CampingSpot>): string | null => {
  const source = normalizeText(getSpotSource(spot));
  const externalId = normalizeText(spot.externalId);
  if (!source || !externalId) return null;
  return `${source}:${externalId}`;
};

export const mergeSpotsByIdentity = (existing: CampingSpot[], incoming: CampingSpot[]): CampingSpot[] => {
  const byId = new Map<string, CampingSpot>();
  const byExternal = new Map<string, string>();

  const upsert = (spot: CampingSpot) => {
    // Vaktpost: objekter uten gyldig streng-id skal ALDRI inn i spots-state.
    // Nedstrøms filtre (`s.id.startsWith(...)` i App/Map) krasjer på undefined —
    // traff prod 2026-07-16 da seedede cached_spots-docs manglet id-feltet.
    // Alle setSpots-veier går gjennom denne merge-funksjonen, så guarden her
    // beskytter mot enhver fremtidig kilde av id-løse spots.
    if (typeof spot?.id !== 'string' || spot.id === '') {
      console.warn('[spotIdentity] Dropper spot uten gyldig id:', spot?.name || spot);
      return;
    }
    const externalKey = getExternalIdentityKey(spot);

    if (externalKey && byExternal.has(externalKey)) {
      const existingId = byExternal.get(externalKey)!;
      const prev = byId.get(existingId);
      if (prev) {
        byId.set(existingId, {
          ...prev,
          ...spot,
          id: existingId,
          isVerified: spot.isVerified || prev.isVerified,
          photoUrls: (spot.photoUrls?.length ? spot.photoUrls : prev.photoUrls),
          visitCount: spot.visitCount ?? prev.visitCount,
          vehicleVisits: spot.vehicleVisits ?? prev.vehicleVisits,
          vehicleMaxLength: spot.vehicleMaxLength ?? prev.vehicleMaxLength,
          reviews: spot.reviews ?? prev.reviews,
          bathingMetadata: spot.bathingMetadata ?? prev.bathingMetadata,
          hikingTrails: spot.hikingTrails ?? prev.hikingTrails,
          nearestCharger: spot.nearestCharger ?? prev.nearestCharger,
          accessRoute: spot.accessRoute ?? prev.accessRoute
        });
        return;
      }
    }

    const prevById = byId.get(spot.id);
    if (prevById) {
      byId.set(spot.id, {
        ...prevById,
        ...spot,
        isVerified: spot.isVerified || prevById.isVerified,
        photoUrls: (spot.photoUrls?.length ? spot.photoUrls : prevById.photoUrls),
        visitCount: spot.visitCount ?? prevById.visitCount,
        vehicleVisits: spot.vehicleVisits ?? prevById.vehicleVisits,
        vehicleMaxLength: spot.vehicleMaxLength ?? prevById.vehicleMaxLength,
        reviews: spot.reviews ?? prevById.reviews,
        bathingMetadata: spot.bathingMetadata ?? prevById.bathingMetadata,
        hikingTrails: spot.hikingTrails ?? prevById.hikingTrails,
        nearestCharger: spot.nearestCharger ?? prevById.nearestCharger,
        accessRoute: spot.accessRoute ?? prevById.accessRoute
      });
    } else {
      byId.set(spot.id, spot);
    }

    if (externalKey) {
      byExternal.set(externalKey, spot.id);
    }
  };

  existing.forEach(upsert);
  incoming.forEach(upsert);
  return Array.from(byId.values());
};
